// src/app/pages/LoginPage.tsx
import { useState } from 'react';
import './login.css';
import {
  Eye,
  EyeOff,
  Loader2,
  Users,
  Boxes,
  ShoppingCart,
  BarChart3,
  Phone,
  Lock,
  ArrowLeftRight,
  Shield,
} from 'lucide-react';
import { useLoginForm } from '../features/auth/hooks/useLoginForm';
import { NetworkPulseOverlay } from './components/NetworkPulseOverlay';
import loginHero from '../../assets/login/login-hero.webp';
import loginBrand from '../../assets/login/login-brand.webp';
import loginLogo from '../../assets/login/login-logo.webp';

const FEATURES = [
  {
    icon: Users,
    title: 'مدیریت مشتریان',
  },
  {
    icon: Boxes,
    title: 'سرویس‌ها و پلن‌ها',
  },
  {
    icon: ShoppingCart,
    title: 'سفارش‌ها و پرداخت‌ها',
  },
  {
    icon: BarChart3,
    title: 'گزارش‌ها و درآمد',
  },
] as const;

export function LoginPage() {
  const {
    mobile,
    setMobile,
    password,
    setPassword,
    isSubmitting,
    errorMessage,
    handleSubmit,
  } = useLoginForm();
  const [showPassword, setShowPassword] = useState(false);
  // NOTE: rememberMe is local state only. Wire this into useLoginForm /
  // the auth request payload once the backend field is confirmed.
  const [rememberMe, setRememberMe] = useState(false);

  return (
    <div dir="rtl" className="login-page">
      {/* Branding / product side — vertical composition per reference */}
      <div className="login-branding">
        <div className="login-brand">
          <img src={loginBrand} alt="VaniaLabs" className="login-brand__logo" />
        </div>

        <div className="login-branding__content">
          <h1 className="login-headline">
            مدیریت هوشمند، سریع و بدون محدودیت
          </h1>
          <p className="login-tagline">
            پنل اختصاصی نمایندگان VaniaLabs برای مدیریت مشتریان، سرویس‌ها،
            سفارش‌ها و درآمد در یک محیط حرفه‌ای و هوشمند.
          </p>

          <ul className="login-features">
            {FEATURES.map(({ icon: Icon, title }) => (
              <li key={title} className="login-feature">
                <span className="login-feature__icon">
                  <Icon size={26} strokeWidth={1.75} />
                </span>
                <span className="login-feature__title">{title}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="login-illustration">
          <div className="login-illustration__frame">
            <img src={loginHero} alt="" className="login-illustration__img" />
            <NetworkPulseOverlay />
          </div>
        </div>
      </div>

      {/* Login side */}
      <div className="login-panel">
        <div className="login-mobile-brand">
          <img src={loginBrand} alt="VaniaLabs" className="login-brand__logo login-brand__logo--sm" />
        </div>

        <div className="login-card">
          <div className="login-header">
            <img src={loginLogo} alt="" aria-hidden="true" className="login-card-logo" />
            <h2 className="login-title">خوش آمدید</h2>
            <p className="login-subtitle">
              ورود به پنل مدیریت نمایندگان
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="mobile" className="login-label">
                شماره موبایل
              </label>
              <div className="relative">
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  inputMode="numeric"
                  dir="ltr"
                  autoComplete="tel"
                  placeholder="09120000000"
                  required
                  disabled={isSubmitting}
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  className="login-input mobile-input"
                />
                <span className="login-field-icon login-field-icon--start">
                  <Phone size={16} strokeWidth={2} />
                </span>
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">
                رمز عبور
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                  disabled={isSubmitting}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="login-input password-input"
                />
                <span className="login-field-icon login-field-icon--start">
                  <Lock size={16} strokeWidth={2} />
                </span>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((value) => !value)}
                  className="login-field-icon login-field-icon--end login-field-toggle"
                  aria-label={showPassword ? 'مخفی کردن رمز عبور' : 'نمایش رمز عبور'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <label className="login-remember">
              <span>مرا در این دستگاه به خاطر بسپار</span>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={isSubmitting}
              />
            </label>

            {errorMessage && (
              <p role="alert" className="login-error">
                {errorMessage}
              </p>
            )}

            <button type="submit" disabled={isSubmitting} className="login-button">
              {isSubmitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ArrowLeftRight size={16} />
              )}
              {isSubmitting ? 'در حال ورود...' : 'ورود به پنل'}
            </button>

            <a href="/forgot-password" className="login-forgot">
              <Lock size={13} strokeWidth={2} />
              <span>رمز عبور خود را فراموش کرده‌اید؟</span>
            </a>
          </form>

          <div className="login-footer">
            <Shield size={13} strokeWidth={2} />
            <span>VaniaLabs Representative Admin</span>
          </div>
        </div>
      </div>
    </div>
  );
}
